import React from 'react';
import classNames from 'classnames';

import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { pickCategoryFields } from '../../util/fieldHelpers';
import { stringify } from '../../util/urlHelpers';

import { NamedLink } from '../../components';

import css from './ListingCategoryPath.module.css';

/**
 * GardenPlace: the listing's categories as a path of links, e.g.
 * "All listings › Bulbs & Tubers › Dahlias". Each part links to the search page showing
 * all listings in that category.
 *
 * @param {Object} publicData listing's public data (categoryLevel1, categoryLevel2, ...)
 * @param {Object} categoryConfiguration config.categoryConfiguration
 * @returns {Array<{ label: string, search: Object }>} one item per category level
 */
export const getCategoryPath = (publicData = {}, categoryConfiguration = {}) => {
  const { key: prefix, categories = [] } = categoryConfiguration;
  if (!prefix || categories.length === 0) {
    return [];
  }
  // Only values that match the current categories in Console
  const values = Object.values(pickCategoryFields(publicData, prefix, 1, categories));

  let options = categories;
  return values.map((id, i) => {
    const category = options.find(c => c.id === id);
    options = category?.subcategories || [];
    const search = values
      .slice(0, i + 1)
      .reduce((params, value, level) => ({ ...params, [`pub_${prefix}${level + 1}`]: value }), {});
    return { label: (category?.name || id).trim(), search };
  });
};

/**
 * Category path shown on the listing page. Renders nothing if the listing has no category.
 *
 * @component
 * @param {Object} props
 * @param {string} [props.className] - Custom class that extends the default class for the root element
 * @param {Object} props.publicData - The listing's public data
 * @param {Object} props.categoryConfiguration - config.categoryConfiguration
 * @param {boolean} [props.isHeroLayout] - Shown below the cover photo (instead of above the photos)
 * @returns {JSX.Element|null}
 */
const ListingCategoryPath = props => {
  const { className, publicData, categoryConfiguration, isHeroLayout = false } = props;
  const intl = useIntl();
  const path = getCategoryPath(publicData, categoryConfiguration);

  if (path.length === 0) {
    return null;
  }

  return (
    <nav
      className={classNames(isHeroLayout ? css.rootForHeroLayout : css.root, className)}
      aria-label={intl.formatMessage({ id: 'ListingCategoryPath.ariaLabel' })}
    >
      <ol className={css.list}>
        <li className={css.item}>
          <NamedLink className={css.link} name="SearchPage">
            <FormattedMessage id="ListingCategoryPath.allListings" />
          </NamedLink>
        </li>
        {path.map(({ label, search }) => (
          <li key={label} className={css.item}>
            <span className={css.separator} aria-hidden="true">
              ›
            </span>
            <NamedLink className={css.link} name="SearchPage" to={{ search: stringify(search) }}>
              {label}
            </NamedLink>
          </li>
        ))}
      </ol>
    </nav>
  );
};

export default ListingCategoryPath;
